/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { t } from 'i18next';

import { getDispatchDescription } from '~screens/Dashboard/screens/Home/screens/Checkout/components/DispatchDetails/utils';
import { CLIENT_PAGES } from '~constants/pages';
import {
  PAYMENT_METHODS,
  PAYMENT_METHODS_INFO,
  CARD_PAYMENT_METHODS_INFO,
  PAYMENT_CARD_ICONS,
  PAYMENT_METHODS_ONLINE,
  CARD_PAYMENT_METHODS_ONLINE
} from '~constants/paymentMethods';
import {
  COUNTRIES,
  CURRENT_COUNTRY_ID,
  TIP_ALLOWED,
  QUICK_ORDER_ENABLED,
  CARD_PAYMENT_TYPE
} from '~constants/environment';
import { DISPATCH_METHODS } from '~constants/address';
import { QUICK_ORDER_MODAL, PRIVACY_POLICY_OR_TERMS_MODAL } from '~redux/Modal/constants';
import { CURRENT_USER_TARGET, ADDRESS_TARGET, GUEST_USER_TARGET } from '~redux/Auth/constants';
import { NOTIFICATION_DANGER } from '~redux/Notification/constants';
import {
  CURRENT_SUBSIDIARY_TARGET,
  DISPATCH_METHOD_TARGET,
  SELECTED_DELIVERY_TIME
} from '~redux/SearchStore/constants';
import { actionCreators as homeActions } from '~screens/Dashboard/screens/Home/redux/actions';
import { actionCreators as modalActions } from '~redux/Modal/actions';
import { actionCreators as orderActions } from '~redux/Orders/actions';
import { actionCreators as notificationActions } from '~redux/Notification/actions';
import { actionCreators as searchStoreActions } from '~redux/SearchStore/actions';
import {
  PAYMENT_CARDS_TARGET,
  EXTERNAL_OFFER_TARGET,
  TIP_TARGET,
  CURRENT_ORDER_TARGET
} from '~screens/Dashboard/screens/Home/redux/constants';
import {
  getCreditCardString,
  mercantilEnabled,
  normalizeMethods,
  isMethodValid
} from '~utils/paymentMethods';
import { arrayHasLength } from '~utils/array';
import { validateDispatchMethod } from '~models/shoppingCart';
import { getMissingCartItems, hasACouponWithOverride } from '~utils/shoppingCart';
import { isFakeStore, isOpenActualStore } from '~utils/actualStore';
import { checkoutPaymentMethodViaCookie } from '~utils/cookies';
import CardInformation from '~components/CardInformation';
import useTipSelection from '~screens/Dashboard/screens/Home/screens/Checkout/components/TipSelection/hooks';
import { getRemainingToGetDelivery } from '~screens/Dashboard/screens/Home/screens/Checkout/components/OrderDetail/utils';
import { SELECT_DISPATCH_TIME_ENABLED } from '~constants/features';
import { shoppingCartSelector, orderSettingsSelector } from '~screens/Dashboard/screens/Home/redux/selectors';
import { ingenico3DSv2 } from '~screens/Dashboard/screens/Home/screens/Checkout/utils';

const useQuickOrder = () => {
  const isOpen = useSelector(state => state.modal[`${QUICK_ORDER_MODAL}ModalIsOpen`]);
  const username = useSelector(state => state.auth[CURRENT_USER_TARGET]?.name);
  const currentUser = useSelector(state => state.auth[CURRENT_USER_TARGET]);
  const address = useSelector(state => state.auth[ADDRESS_TARGET]?.current);
  const currentAddress = useSelector(state => state.auth[ADDRESS_TARGET]?.current);
  const searchStore = useSelector(state => state.searchStore);
  const dispatchNote = useSelector(state => state.auth[ADDRESS_TARGET]?.current?.dispatchNote);
  const apartmentDetails = useSelector(state => state.auth[ADDRESS_TARGET]?.current?.apartmentDetails);
  const paymentCardMethods = useSelector(state => state.home[PAYMENT_CARDS_TARGET]);
  const currentSubsidiary = useSelector(state => state.searchStore[CURRENT_SUBSIDIARY_TARGET]);
  const dispatchMethod = useSelector(state => state.searchStore[DISPATCH_METHOD_TARGET]);
  const total = useSelector(state => state.home?.shoppingCart?.total);
  const shoppingCart = useSelector(state => state.home?.shoppingCart);
  const storeId = useSelector(state => state.searchStore?.currentSubsidiary?.id);
  const externalOffer = useSelector(state => state.home[EXTERNAL_OFFER_TARGET]);
  const checkoutButtonStatus = useSelector(state => state.order?.checkoutButtonStatusLoading);
  const paymentMethods = useSelector(state => state.searchStore[CURRENT_SUBSIDIARY_TARGET]?.paymentMethods);
  const guestUser = useSelector(state => state.auth[GUEST_USER_TARGET]);
  const couponPaymentMethod = shoppingCart?.appliedPromos?.[0]?.appliedCoupon?.paymentMethod;
  const currentTip = useSelector(state => state.home[TIP_TARGET]);
  const orderLoading = useSelector(
    state => state.home[`${CURRENT_ORDER_TARGET}Loading`] || state.order[`${CURRENT_ORDER_TARGET}Loading`]
  );
  const minimumValueForDelivery = useSelector(
    state =>
      shoppingCartSelector(state)?.minimumPriceForDelivery ||
      orderSettingsSelector(state)?.minPriceForDelivery
  );
  const selectedDeliveryTime = useSelector(state => state.searchStore[SELECTED_DELIVERY_TIME]);
  const isCallCenter = !!useSelector(state => state.auth?.callCenterGuestUser);

  const [selectedCardId, setSelectedCardId] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [onlinePayment, setOnlinePayment] = useState(true);
  const [paymentMethodError, setPaymentMethodError] = useState(false);
  const [creditCardKind, setCreditCardKind] = useState(null);
  const [changingPaymentMethod, setChangingPaymentMethod] = useState(false);
  const [cardNumber, setCardNumber] = useState('');

  const dispatch = useDispatch();
  const checkoutButtonToggleLoading = value => dispatch(orderActions.checkoutButtonToggleLoading(value));
  const openPoliciesOrTermsModal = data =>
    dispatch(modalActions.openModalWithData(PRIVACY_POLICY_OR_TERMS_MODAL, data));
  const handleOpenPrivacyPoliciesModal = async () => {
    await dispatch(modalActions.closeModal(QUICK_ORDER_MODAL));
    openPoliciesOrTermsModal({ typeMarkdown: CLIENT_PAGES.PRIVACY_POLICY.path });
  };
  const handleOpenTermsModal = async () => {
    await dispatch(modalActions.closeModal(QUICK_ORDER_MODAL));
    openPoliciesOrTermsModal({ typeMarkdown: CLIENT_PAGES.TERMS.path });
  };
  const createOrder = orderData => dispatch(homeActions.createOrder(orderData));

  const isPapaCar = currentSubsidiary?.papacar;
  const { showTipModule } = useTipSelection({
    selectedPaymentMethod,
    isPapaCar,
    dispatchMethod
  });

  const isRegistered = currentUser && !guestUser;
  let isMercantil = false;
  if (!currentSubsidiary?.id) {
    isMercantil = mercantilEnabled(currentSubsidiary);
  }
  const cardOnlinePaymentMethod = isMercantil
    ? PAYMENT_METHODS.mercantil
    : CARD_PAYMENT_METHODS_INFO[CURRENT_COUNTRY_ID];

  const disableCheckoutButton = useMemo(
    () => checkoutButtonStatus || shoppingCart.items.length === 0 || isFakeStore(currentSubsidiary),
    [checkoutButtonStatus, currentSubsidiary, shoppingCart.items.length]
  );

  const hasAcceptedPrivacyPolicies =
    currentUser?.acceptPrivacyPolicies ||
    (currentUser?.privacyPolicies && ![COUNTRIES.es, COUNTRIES.pt].includes(CURRENT_COUNTRY_ID));

  const hasPaymentsAvailable =
    (paymentMethods && Object.keys(paymentMethods).length > 0) || paymentCardMethods;

  const isDelivery = dispatchMethod === DISPATCH_METHODS.PJ_DELIVERY;
  const isHotspot = currentSubsidiary?.hotspot;

  const isTipAllowed =
    TIP_ALLOWED && (PAYMENT_METHODS_ONLINE[selectedPaymentMethod] || selectedCardId) && isDelivery;

  const minValueToGetDelivery = getRemainingToGetDelivery(minimumValueForDelivery, shoppingCart);
  const disclaimerMinDeliveryActive =
    !hasACouponWithOverride(shoppingCart) && minValueToGetDelivery > 0 && isDelivery;
  const isQuickOrderAvailable =
    QUICK_ORDER_ENABLED !== 'false' &&
    currentSubsidiary &&
    !isFakeStore(currentSubsidiary) &&
    !isPapaCar &&
    currentSubsidiary?.id &&
    currentUser &&
    !!currentUser.phone &&
    !!currentUser.email &&
    isOpenActualStore(searchStore) &&
    (CURRENT_COUNTRY_ID === COUNTRIES.es || CURRENT_COUNTRY_ID === COUNTRIES.pt
      ? !!hasAcceptedPrivacyPolicies
      : true) &&
    hasPaymentsAvailable &&
    (isDelivery ? !disclaimerMinDeliveryActive : true);
  const storePaymentMethods = currentSubsidiary?.paymentMethods
    ? Object.keys(currentSubsidiary?.paymentMethods)
    : [];
  const handleCardSelection = event => {
    setSelectedCardId(event.target.value);
  };
  const getPaymentCards = () => dispatch(homeActions.getPaymentCards(isMercantil));

  const checkBlockedItem = async ({ items }) => {
    const { missing } = await getMissingCartItems({ storeId, dispatchMethod, offerId: externalOffer }, items);

    return missing;
  };

  const handleChangingPaymentMethod = value => setChangingPaymentMethod(value);

  const handleSelectPaymentMethod = ({
    cardId,
    kind,
    paymentMethod,
    cardNumber: cardNumberString,
    onlinePayment: onlinePaymentBool,
    changingActive
  }) => {
    setSelectedPaymentMethod(paymentMethod);
    setOnlinePayment(onlinePaymentBool);
    setSelectedCardId(cardId);
    setCardNumber(cardNumberString);
    setPaymentMethodError(false);
    setCreditCardKind(kind);
    if (changingActive) {
      handleChangingPaymentMethod(false);
    }
  };

  const refreshSubsidiary = () =>
    dispatch(searchStoreActions.refreshSubsidiary(storeId, dispatchMethod, currentSubsidiary));
  const showNotification = ({ message, type }) => dispatch(notificationActions.show({ message, type }));
  const handleGenerateOrder = async () => {
    const validDispatchMethod = validateDispatchMethod(shoppingCart.items, dispatchMethod);
    await refreshSubsidiary(storeId, dispatchMethod);
    const normalizedMethods = normalizeMethods(storePaymentMethods, PAYMENT_METHODS_INFO[CURRENT_COUNTRY_ID]);
    if (!isMethodValid(selectedPaymentMethod, normalizedMethods)) {
      showNotification({ message: t('Checkout:paymentMethodUnavailable'), type: NOTIFICATION_DANGER });
      this.handleSelectPaymentMethod({ paymentMethod: null, onlinePayment: false });
      return;
    }

    const items = shoppingCart.items.map(item => ({
      ...item,
      variantName: `${item.variantName}${item.halves || item.ingredients ? `\n${item.description}` : ''}`
    }));

    const blockedItems = await checkBlockedItem({
      items,
      storeId,
      dispatchMethod: validDispatchMethod
    });

    if (arrayHasLength(blockedItems)) {
      this.setState({ blockedProducts: blockedItems });
      this.handleOpenBlockedProductModal();
      return;
    }

    const orderData = {
      address,
      shoppingCart: { ...shoppingCart, items },
      storeId,
      userAddress: currentAddress?.textAddress,
      user: currentUser,
      dispatchMethod: validDispatchMethod,
      ...(isDelivery && { dispatchNote, apartmentDetails }),
      onlinePayment,
      paymentMethod: selectedPaymentMethod,
      ...(isTipAllowed && { tip: currentTip }),
      ...(selectedCardId && { cardId: selectedCardId }),
      deliveryLatitude: address.latitude,
      deliveryLongitude: address.longitude,
      acceptPoll: true,
      privacyPolicies: true,
      ...(SELECT_DISPATCH_TIME_ENABLED &&
        selectedDeliveryTime?.date && { requestedTime: moment(selectedDeliveryTime.date).format() }),
      ...(CARD_PAYMENT_TYPE === CARD_PAYMENT_METHODS_ONLINE.directClickToPay && {
        ingenico3DSv2: ingenico3DSv2()
      })
    };
    checkoutButtonToggleLoading(true);
    createOrder(orderData);
  };

  const renderPaymentCardMethods = () =>
    paymentCardMethods.map(({ id, lastDigits, kind }) => (
      <CardInformation
        key={id}
        cardId={id}
        kind={kind}
        cardNumber={getCreditCardString(lastDigits)}
        cardOnlinePaymentMethod={cardOnlinePaymentMethod}
        selectedCardId={selectedCardId}
        iconClassName={PAYMENT_CARD_ICONS[kind]}
        onSelectCardPayment={handleSelectPaymentMethod}
        disabled={!!couponPaymentMethod}
        changingActive={changingPaymentMethod}
      />
    ));

  useEffect(() => {
    !isCallCenter && getPaymentCards();
  }, []);
  useEffect(() => {
    if (paymentCardMethods.length > 0) {
      setSelectedCardId(paymentCardMethods[0].id);
    }
  }, [paymentCardMethods]);
  useEffect(() => {
    if (!paymentMethods || !isOpen) {
      return;
    }

    const paymentMethodCookie = checkoutPaymentMethodViaCookie();
    if (paymentMethodCookie) {
      const paymentKey = Object.keys(paymentMethods)?.find(
        item =>
          PAYMENT_METHODS_INFO[CURRENT_COUNTRY_ID][item]?.name === paymentMethodCookie && paymentMethods[item]
      );
      const paymentMethodObject = PAYMENT_METHODS_INFO[CURRENT_COUNTRY_ID][paymentKey];
      if (paymentMethodObject) {
        const { name: paymentMethod, isOnline } = paymentMethodObject;
        if (paymentMethod === PAYMENT_METHODS.powertranzGuest) {
          return;
        }
        if (paymentMethod === PAYMENT_METHODS.mercantilGuest) {
          return;
        }
        handleSelectPaymentMethod({ paymentMethod, onlinePayment: isOnline });
      }
    }
  }, []);

  function getDispatchTitleAndDetails() {
    if (!isQuickOrderAvailable) {
      return { title: '', details: '', waitTime: '' };
    }
    const { deliveryWaitTime, pickUpWaitTime } = currentSubsidiary;
    const showDeliveryWaitTime = (isHotspot || isDelivery) && !isPapaCar;
    const waitTime = showDeliveryWaitTime ? deliveryWaitTime : pickUpWaitTime;
    const { title, details = '' } = getDispatchDescription(isDelivery, currentSubsidiary, currentAddress);
    return { title, details, waitTime };
  }

  const { title, details, waitTime } = getDispatchTitleAndDetails();

  return {
    isOpen,
    username,
    dispatchNote,
    selectedCardId,
    total,
    paymentCardMethods,
    disableCheckoutButton,
    paymentMethods,
    selectedPaymentMethod,
    paymentMethodError,
    creditCardKind,
    dispatchMethod,
    isRegistered,
    changingPaymentMethod,
    cardNumber,
    showTipModule,
    isQuickOrderAvailable,
    orderLoading,
    handleCardSelection,
    handleOpenPrivacyPoliciesModal,
    handleOpenTermsModal,
    handleGenerateOrder,
    handleSelectPaymentMethod,
    setSelectedPaymentMethod,
    handleChangingPaymentMethod,
    renderPaymentCardMethods,
    title,
    details,
    waitTime,
    selectedDeliveryTime
  };
};

export default useQuickOrder;
